import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ApiError, api, createSession } from "./api";
import { hobbyPresentation } from "./presentation";
import { closeToBotChat, haptic, openExternalLink, openTelegramInvoice, shareTelegramLink, telegramWebApp } from "./telegram";
import type {
  AccessView,
  CollectionDetail,
  CardPayload,
  HobbyCode,
  HobbySummary,
  Overview,
  MonthlyReportView,
  RegularityRewardView,
  ReferralRewardView,
  ReferralView,
  SubscriptionView,
  TabId,
  TaskView,
  UserProfile
} from "./types";

type LoadState = "loading" | "ready" | "error";

const tabs: Array<{ id: TabId; emoji: string; label: string }> = [
  { id: "today", emoji: "☀️", label: "Сегодня" },
  { id: "hobbies", emoji: "✨", label: "Хобби" },
  { id: "collection", emoji: "📚", label: "Коллекция" },
  { id: "profile", emoji: "👤", label: "Профиль" }
];

function formatDate(value: string, options: Intl.DateTimeFormatOptions = {}) {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    ...options
  }).format(new Date(value));
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));
}

function currentDateLabel() {
  const result = new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long"
  }).format(new Date());
  return result.charAt(0).toUpperCase() + result.slice(1);
}

function Cover({ code, compact = false, card }: { code: HobbyCode; compact?: boolean; card?: CardPayload }) {
  const presentation = hobbyPresentation[code];
  const fallback = `/covers/${code}-${code === "objects" ? "v2" : "v3"}.png`;
  const source = card?.preview?.mode === "external_preview_allowed" && card.preview.imageUrl
    ? card.preview.imageUrl
    : fallback;
  if (card) {
    return <div className={`hobby-cover card-cover-image ${compact ? "is-compact" : ""}`}><img src={source} alt={card.preview?.altText ?? "Обложка выпуска"} /></div>;
  }
  return (
    <div className={`hobby-cover ${presentation.visualClass} ${compact ? "is-compact" : ""}`} aria-hidden="true">
      <div className="cover-orbit cover-orbit-one" />
      <div className="cover-orbit cover-orbit-two" />
      <span className="cover-emoji">{presentation.emoji}</span>
      <span className="cover-glint" />
    </div>
  );
}

function Button({
  children,
  onClick,
  kind = "primary",
  disabled = false,
  type = "button"
}: {
  children: ReactNode;
  onClick?: () => void;
  kind?: "primary" | "secondary" | "ghost";
  disabled?: boolean;
  type?: "button" | "submit";
}) {
  return (
    <button type={type} className={`button button-${kind}`} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

function FactSection({ label, children }: { label: string; children?: ReactNode }) {
  if (!children) return null;
  return <section className="card-section"><span>{label}</span><div>{children}</div></section>;
}

function SourcePreview({ card }: { card: CardPayload }) {
  if (!card.primarySourceUrl) return null;
  const label = card.kind === "painting"
    ? "Картина в музее"
    : card.kind === "letter"
      ? "Письмо в архиве"
      : "Предмет и источник";
  return (
    <button type="button" className={`source-preview source-${card.kind ?? "object"}`} onClick={() => openExternalLink(card.primarySourceUrl!)}>
      <span className="source-preview-symbol">{card.kind === "painting" ? "🎨" : card.kind === "letter" ? "✉️" : "🔎"}</span>
      <span className="source-preview-copy"><small>{label}</small><strong>{card.primarySourceLabel ?? "Открыть оригинал"}</strong><em>Открыть оригинал в полном качестве</em></span>
      <b>↗</b>
    </button>
  );
}

function RichCardContent({ card }: { card: CardPayload }) {
  const sections = card.miniAppSections?.length ? card.miniAppSections : card.detailSections;
  const hasUnifiedSections = Boolean(sections?.length);
  const lead = card.miniAppLead ?? card.hook;
  return (
    <div className="rich-card-content">
      {card.subtitle && <p className="card-subtitle">{card.subtitle}</p>}
      {lead && <p className="card-intro">{lead}</p>}
      {hasUnifiedSections && sections!.map((section) => (
        <FactSection key={section.key} label={section.title}><p>{section.body}</p></FactSection>
      ))}
      {!hasUnifiedSections && card.kind === "painting" && (
        <>
          <FactSection label="Контекст"><p>{card.context}</p></FactSection>
          <FactSection label="Проверенные факты"><p>{card.facts}</p></FactSection>
        </>
      )}
      {!hasUnifiedSections && card.kind === "letter" && (
        <>
          <FactSection label="Автор"><p>{card.author}</p></FactSection>
          <FactSection label="Адресат"><p>{card.recipient}</p></FactSection>
          <FactSection label="Это письмо"><p>{card.concreteLetter}</p></FactSection>
          <FactSection label="Контекст"><p>{card.context}</p></FactSection>
        </>
      )}
      {!hasUnifiedSections && card.kind === "object" && (
        <>
          <FactSection label="Кто и когда"><p>{[card.participants, card.period].filter(Boolean).join(" · ")}</p></FactSection>
          <FactSection label="История за три минуты"><p>{card.story}</p></FactSection>
        </>
      )}
      {!hasUnifiedSections && <FactSection label="Что сделать"><p>{card.action}</p></FactSection>}
      <div className="question-block"><span>Вопрос выпуска</span><p>{card.question}</p></div>
      {card.sourceLinks && card.sourceLinks.length > 0 && (
        <div className="source-list">
          <span>Источники</span>
          {card.sourceLinks.map((source) => (
            <button type="button" key={source.url} onClick={() => openExternalLink(source.url)}>{source.label}<b>↗</b></button>
          ))}
        </div>
      )}
    </div>
  );
}

function CardSheet({ task, close, answer }: { task: TaskView; close: () => void; answer: (taskId: string) => void }) {
  return (
    <Sheet onClose={close} label={task.card.title ?? task.hobby.name}>
      <Cover code={task.hobby.code} card={task.card} />
      <div className="sheet-content card-detail-content">
        <SourcePreview card={task.card} />
        <p className="eyebrow">{task.hobby.name} · выпуск {String(task.sequenceNo).padStart(2, "0")}</p>
        <h2>{task.card.title ?? task.hobby.name}</h2>
        <RichCardContent card={task.card} />
        {task.status !== "completed" && <Button onClick={() => answer(task.id)}>Ответить в боте</Button>}
        <Button kind="ghost" onClick={close}>Закрыть</Button>
      </div>
    </Sheet>
  );
}

function Sheet({ children, onClose, label }: { children: ReactNode; onClose: () => void; label: string }) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="sheet" role="dialog" aria-modal="true" aria-label={label}>
        <div className="sheet-grabber" />
        <button className="sheet-close" type="button" onClick={onClose} aria-label="Закрыть">×</button>
        {children}
      </section>
    </div>
  );
}

function LoadingScreen() {
  return (
    <main className="state-screen">
      <div className="brand-mark"><span>✦</span></div>
      <p className="eyebrow">Собираем вашу коллекцию</p>
      <h1>Ещё мгновение</h1>
      <div className="loading-line"><span /></div>
    </main>
  );
}

function ErrorScreen({ message, retry }: { message: string; retry: () => void }) {
  return (
    <main className="state-screen">
      <div className="brand-mark is-error"><span>○</span></div>
      <p className="eyebrow">Безопасный вход не завершён</p>
      <h1>Не удалось открыть приложение</h1>
      <p className="state-copy">{message}</p>
      <Button onClick={retry}>Попробовать ещё раз</Button>
      <Button kind="ghost" onClick={() => telegramWebApp()?.close?.()}>Вернуться в Telegram</Button>
    </main>
  );
}

function LegalConsent({ me, busy, accept }: { me: UserProfile; busy: boolean; accept: () => void }) {
  return (
    <main className="state-screen legal-consent-screen">
      <div className="brand-mark"><span>✦</span></div>
      <p className="eyebrow">Один формальный шаг</p>
      <h1>Продолжим знакомство</h1>
      <p className="state-copy">Подтвердите актуальные условия. Мы сохраним только факт и версию согласия.</p>
      <div className="legal-links">
        <button type="button" onClick={() => openExternalLink(me.legal.termsUrl)}>Условия использования ↗</button>
        <button type="button" onClick={() => openExternalLink(me.legal.privacyUrl)}>Политика конфиденциальности ↗</button>
      </div>
      <Button onClick={accept} disabled={busy}>{busy ? "Сохраняем…" : "Принять и продолжить"}</Button>
      <p className="legal-caption">Нажимая кнопку, вы принимаете Условия использования и подтверждаете, что ознакомились с Политикой конфиденциальности.</p>
    </main>
  );
}

function HobbyDetails({
  hobby,
  actionLabel,
  action,
  secondaryActionLabel,
  secondaryAction,
  busy,
  close
}: {
  hobby: HobbySummary;
  actionLabel: string;
  action: () => void;
  secondaryActionLabel?: string;
  secondaryAction?: () => void;
  busy: boolean;
  close: () => void;
}) {
  const presentation = hobbyPresentation[hobby.code];
  return (
    <Sheet onClose={close} label={hobby.name}>
      <Cover code={hobby.code} />
      <div className="sheet-content">
        <p className="eyebrow">{presentation.eyebrow}</p>
        <h2>{hobby.name}</h2>
        <p className="rhythm">Раз в неделю · около 20 минут</p>
        <p className="detail-copy">{presentation.detail}</p>
        <div className="example-card">
          <span>Пример вопроса</span>
          <p>«{presentation.example}»</p>
        </div>
        <p className="saved-copy">{presentation.saved}</p>
        {actionLabel.startsWith("Приобрести") && (
          <>
            <p className="purchase-note">{hobby.priceStars ?? 149} ⭐ за 30 дней. Следующее списание — ориентировочно {formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString())}. Продление можно отключить в профиле; после последнего доступного выпуска оно отключится автоматически. Выпуски не повторяются.</p>
            <button className="purchase-terms" type="button" onClick={() => openExternalLink(`${window.location.origin}/legal/terms/`)}>Условия подписки ↗</button>
          </>
        )}
        <div className="sheet-actions">
          <Button onClick={action} disabled={busy}>{busy ? "Подождите…" : actionLabel}</Button>
          {secondaryActionLabel && secondaryAction && (
            <Button kind="ghost" onClick={secondaryAction} disabled={busy}>{secondaryActionLabel}</Button>
          )}
        </div>
      </div>
    </Sheet>
  );
}

function Onboarding({
  hobbies,
  busy,
  onActivate,
  onNotifyNewHobbies,
  newHobbyNotificationsEnabled
}: {
  hobbies: HobbySummary[];
  busy: boolean;
  onActivate: (hobby: HobbySummary) => Promise<void>;
  onNotifyNewHobbies: () => Promise<boolean>;
  newHobbyNotificationsEnabled: boolean;
}) {
  const [index, setIndex] = useState(0);
  const [details, setDetails] = useState<HobbySummary | null>(null);
  const [confirming, setConfirming] = useState<HobbySummary | null>(null);
  const cardsCount = hobbies.length + 1;
  const change = (direction: number) => {
    haptic("light");
    setIndex((current) => (current + direction + cardsCount) % cardsCount);
  };
  const hobby = index < hobbies.length ? hobbies[index] : null;
  const enableNewHobbyNotifications = async () => {
    if (newHobbyNotificationsEnabled) return;
    await onNotifyNewHobbies();
  };

  return (
    <main className="onboarding page-shell">
      <header className="onboarding-header">
        <p className="eyebrow">Ваш маленький интеллектуальный ритуал</p>
        <h1>Выберите ваше первое хобби</h1>
        <p>Первое хобби — бесплатно. Если оно вам не подойдёт, в течение 24 часов его можно заменить.</p>
      </header>

      <section className="gallery" aria-label="Доступные хобби">
        <button type="button" className="gallery-arrow gallery-arrow-left" onClick={() => change(-1)} aria-label="Предыдущее хобби">‹</button>
        <div className="gallery-card-wrap">
          {hobby ? (
            <article className="gallery-card glass-card" key={hobby.id}>
              <Cover code={hobby.code} />
              <div className="gallery-card-copy">
                <span className="hobby-number">0{index + 1}</span>
                <h2>{hobby.name}</h2>
                <Button kind="secondary" onClick={() => setDetails(hobby)}>Подробнее</Button>
              </div>
            </article>
          ) : (
            <article className="gallery-card glass-card coming-card">
              <div className="hobby-cover coming-cover" aria-hidden="true">
                <div className="coming-constellation"><span>✦</span><span>✧</span><span>·</span><span>✦</span></div>
              </div>
              <div className="gallery-card-copy">
                <span className="hobby-number">Скоро</span>
                <h2>Ещё 7 хобби</h2>
                <Button kind="secondary" disabled={newHobbyNotificationsEnabled} onClick={() => void enableNewHobbyNotifications()}>
                  {newHobbyNotificationsEnabled ? "Вы получите уведомления ✓" : "Сообщить, когда откроются"}
                </Button>
              </div>
            </article>
          )}
        </div>
        <button type="button" className="gallery-arrow gallery-arrow-right" onClick={() => change(1)} aria-label="Следующее хобби">›</button>
      </section>
      <div className="gallery-dots" aria-hidden="true">
        {Array.from({ length: cardsCount }, (_, cardIndex) => <span key={cardIndex} className={cardIndex === index ? "active" : ""} />)}
      </div>

      {details && (
        <HobbyDetails
          hobby={details}
          actionLabel="Попробовать бесплатно"
          action={() => { setConfirming(details); setDetails(null); }}
          busy={busy}
          close={() => setDetails(null)}
        />
      )}
      {confirming && (
        <Sheet onClose={() => setConfirming(null)} label="Подтверждение выбора">
          <div className="confirm-visual"><span>{hobbyPresentation[confirming.code].emoji}</span></div>
          <div className="sheet-content confirm-content">
            <p className="eyebrow">Подтвердите выбор</p>
            <h2>Начать с «{confirming.name}»?</h2>
            <p className="detail-copy">Это станет вашим бесплатным хобби. Если оно не подойдёт, выбор можно один раз изменить в течение 24 часов.</p>
            <p className="legal-caption">Нажимая «Подтвердить выбор», вы принимаете <button type="button" onClick={() => openExternalLink(`${window.location.origin}/legal/terms/`)}>Условия использования</button> и подтверждаете, что ознакомились с <button type="button" onClick={() => openExternalLink(`${window.location.origin}/legal/privacy/`)}>Политикой конфиденциальности</button>.</p>
            <Button onClick={() => void onActivate(confirming)} disabled={busy}>{busy ? "Начинаем…" : "Подтвердить выбор"}</Button>
          </div>
        </Sheet>
      )}
    </main>
  );
}

function TodayView({ overview, openResult, answer }: { overview: Overview; openResult: (id: string) => void; answer: (taskId: string) => void }) {
  const tasks = overview.activeTasks;
  const [details, setDetails] = useState<TaskView | null>(null);
  return (
    <section className="content-view">
      <header className="section-header">
        <p className="date-label">{currentDateLabel()}</p>
        <h1>{tasks.length > 1 ? "Ваши выпуски на эту неделю" : "Ваш выпуск на эту неделю"}</h1>
      </header>

      {tasks.length === 0 ? (
        <div className="empty-card glass-card">
          <div className="empty-symbol">✦</div>
          <h2>На этой неделе всё сохранено</h2>
          <p>Можно заглянуть в коллекцию или просто дождаться следующей находки.</p>
        </div>
      ) : tasks.map((task) => (
        <article className={`task-card glass-card ${task.status === "completed" ? "is-completed" : ""}`} key={task.id}>
          <Cover code={task.hobby.code} compact />
          <div className="task-copy">
            <div className="task-meta">
              <span>{hobbyPresentation[task.hobby.code].emoji} {task.hobby.name}</span>
              <span>Выпуск {String(task.sequenceNo ?? 1).padStart(2, "0")}</span>
            </div>
            <h2>{task.card.title ?? task.hobby.name}</h2>
            <p>{task.card.hook}</p>
            {task.card.fixture && <span className="fixture-badge">Тестовая карточка</span>}
            {task.status === "completed" ? (
              <>
                <div className="saved-status"><span>✓</span><div><strong>Сохранено в коллекции</strong><small>{task.completedAt ? formatDateTime(task.completedAt) : ""}</small></div></div>
                <Button onClick={() => openResult(task.id)}>Открыть результат</Button>
              </>
            ) : <div className="task-actions"><Button kind="secondary" onClick={() => setDetails(task)}>Открыть карточку</Button><Button onClick={() => answer(task.id)}>Ответить в боте</Button></div>}
          </div>
          <div className="task-facts">
            <div><span>🗓️</span><p><strong>Следующий выпуск — {formatDateTime(task.closesAt)}</strong><small>До этого времени можно ответить на текущий.</small></p></div>
            <div><span>📌</span><p><strong>После ответа выпуск сохранится в коллекции</strong><small>Подойдёт текст или голосовая заметка.</small></p></div>
          </div>
        </article>
      ))}
      {details && <CardSheet task={details} close={() => setDetails(null)} answer={answer} />}
    </section>
  );
}

function HobbiesView({
  hobbies,
  accesses,
  deepLinkHobbyId,
  canChange,
  busy,
  onChange,
  onPurchase,
  onPaywallView,
  onOpenToday,
  onNotifyNewHobbies,
  newHobbyNotificationsEnabled,
  referralReward,
  regularityReward,
  onActivateReferralReward,
  onActivateRegularityReward,
}: {
  hobbies: HobbySummary[];
  accesses: AccessView[];
  deepLinkHobbyId?: string;
  canChange: boolean;
  busy: boolean;
  onChange: (hobby: HobbySummary) => Promise<void>;
  onPurchase: (hobby: HobbySummary) => Promise<void>;
  onPaywallView: (hobby: HobbySummary) => void;
  onOpenToday: () => void;
  onNotifyNewHobbies: () => Promise<boolean>;
  newHobbyNotificationsEnabled: boolean;
  referralReward?: ReferralRewardView;
  regularityReward?: RegularityRewardView;
  onActivateReferralReward: (reward: ReferralRewardView, hobby: HobbySummary) => Promise<void>;
  onActivateRegularityReward: (reward: RegularityRewardView, hobby: HobbySummary) => Promise<void>;
}) {
  const [details, setDetails] = useState<HobbySummary | null>(null);
  const openedDeepLink = useRef<string | null>(null);
  const activeIds = new Set(accesses.filter(
    (access) => access.status === "active" && access.hobby.status !== "exhausted"
  ).map((access) => access.hobby.id));
  const exhaustedIds = new Set(accesses.filter(
    (access) => access.status === "active" && access.hobby.status === "exhausted"
  ).map((access) => access.hobby.id));
  const activeFree = accesses.find((access) => access.type === "free" && access.status === "active");

  useEffect(() => {
    if (!deepLinkHobbyId || openedDeepLink.current === deepLinkHobbyId) return;
    const hobby = hobbies.find((item) => item.id === deepLinkHobbyId);
    if (!hobby) return;
    openedDeepLink.current = deepLinkHobbyId;
    setDetails(hobby);
    if (!activeIds.has(hobby.id) && !exhaustedIds.has(hobby.id)) onPaywallView(hobby);
  }, [activeIds, deepLinkHobbyId, exhaustedIds, hobbies, onPaywallView]);

  const orderedHobbies = [...hobbies].sort((left, right) => {
    const rank = (hobbyId: string) => activeIds.has(hobbyId) ? 2 : exhaustedIds.has(hobbyId) ? 1 : 0;
    return rank(right.id) - rank(left.id);
  });
  const actionFor = (hobby: HobbySummary): {
    label: string;
    action: () => void;
    secondaryLabel?: string;
    secondaryAction?: () => void;
  } => {
    if (activeIds.has(hobby.id)) {
      return { label: "Перейти к выпуску", action: onOpenToday };
    }
    if (exhaustedIds.has(hobby.id)) {
      return { label: "Выбрать другое хобби", action: () => setDetails(null) };
    }
    if (referralReward) {
      return {
        label: "Открыть бесплатно на 30 дней",
        action: () => void onActivateReferralReward(referralReward, hobby)
      };
    }
    if (regularityReward) {
      return {
        label: "Открыть бесплатно на 30 дней",
        action: () => void onActivateRegularityReward(regularityReward, hobby)
      };
    }
    return {
      label: `Приобрести за ${hobby.priceStars ?? 149} ⭐`,
      action: () => void onPurchase(hobby),
      ...(canChange && activeFree
        ? { secondaryLabel: "Заменить бесплатное хобби", secondaryAction: () => void onChange(hobby) }
        : {})
    };
  };

  return (
    <section className="content-view">
      <header className="section-header compact-header">
        <p className="eyebrow">Выберите следующий интерес</p>
        <h1>Ваши хобби</h1>
        <p>Первое хобби — бесплатно. Остальные можете подключить отдельно за Telegram Stars.</p>
      </header>
      {referralReward?.claimExpiresAt && (
        <div className="referral-deadline glass-card">
          <span>🎁</span>
          <p><strong>Ваш подарок ждёт выбора</strong><small>Активируйте дополнительное хобби до {formatDateTime(referralReward.claimExpiresAt)} — затем подарок сгорит.</small></p>
        </div>
      )}
      {regularityReward && !referralReward && (
        <div className="referral-deadline glass-card">
          <span>✨</span>
          <p><strong>Месяц без пропусков</strong><small>Выберите ещё одно хобби — оно откроется бесплатно на 30 дней.</small></p>
        </div>
      )}
      <div className="hobby-list">
        {orderedHobbies.map((hobby) => {
          const active = activeIds.has(hobby.id);
          const exhausted = exhaustedIds.has(hobby.id);
          return (
            <article className={`hobby-row glass-card ${active ? "is-active-hobby" : ""} ${exhausted ? "is-exhausted-hobby" : ""}`} key={hobby.id}>
              <Cover code={hobby.code} compact />
              <div className="hobby-row-copy">
                <div className="row-state">{active
                  ? "Моё хобби"
                  : exhausted
                    ? "Все выпуски пройдены"
                    : `${hobby.priceStars ?? 149} ⭐ · 30 дней`}</div>
                <h2>{hobby.name}</h2>
                {active && activeFree?.hobby.nextTaskAt && <p>Следующий выпуск {formatDateTime(activeFree.hobby.nextTaskAt)}</p>}
                <Button kind={active ? "secondary" : "primary"} onClick={() => {
                  setDetails(hobby);
                  if (!active && !exhausted && !referralReward && !regularityReward) onPaywallView(hobby);
                }}>{active ? "Открыть хобби" : "Подробнее"}</Button>
              </div>
            </article>
          );
        })}
        <article className="coming-row glass-card">
          <div className="coming-small">✦ · ✧</div>
          <div className="coming-row-copy">
            <span>Готовим дальше</span>
            <h2>Ещё 7 хобби — скоро</h2>
            <Button
              kind="secondary"
              disabled={newHobbyNotificationsEnabled}
              onClick={() => void onNotifyNewHobbies()}
            >
              {newHobbyNotificationsEnabled ? "Вы получите уведомления ✓" : "Сообщить, когда откроются"}
            </Button>
          </div>
        </article>
      </div>
      {details && (
        <HobbyDetails
          hobby={details}
          actionLabel={actionFor(details).label}
          action={actionFor(details).action}
          {...(actionFor(details).secondaryLabel && actionFor(details).secondaryAction
            ? {
                secondaryActionLabel: actionFor(details).secondaryLabel,
                secondaryAction: actionFor(details).secondaryAction
              }
            : {})}
          busy={busy}
          close={() => setDetails(null)}
        />
      )}
    </section>
  );
}

function CollectionView({ items, history, openResult, answer, activeTaskId }: { items: TaskView[]; history: TaskView[]; openResult: (id: string) => void; answer: (taskId: string) => void; activeTaskId?: string }) {
  const [showHistory, setShowHistory] = useState(false);
  return (
    <section className="content-view">
      <header className="section-header compact-header">
        <p className="eyebrow">Личный архив наблюдений</p>
        <h1>Коллекция</h1>
        <p>Здесь остаются карточки, к которым вы добавили собственный взгляд.</p>
      </header>
      {items.length === 0 ? (
        <div className="empty-card glass-card">
          <div className="empty-symbol">💭</div>
          <h2>Здесь появится ваше первое открытие</h2>
          <p>Ответьте на текущую карточку текстом или голосовым сообщением.</p>
          {activeTaskId && <Button onClick={() => answer(activeTaskId)}>Ответить на карточку</Button>}
        </div>
      ) : (
        <div className="collection-grid">
          {items.map((item) => (
            <button type="button" className="collection-card glass-card" key={item.id} onClick={() => openResult(item.id)}>
              <Cover code={item.hobby.code} compact />
              <div><span>{item.completedAt ? formatDate(item.completedAt) : "Сохранено"}</span><h2>{item.card.title ?? item.hobby.name}</h2><p>{item.responseCount ?? 1} {item.responseCount === 1 ? "заметка" : "заметки"}</p></div>
            </button>
          ))}
        </div>
      )}
      {history.length > 0 && (
        <div className="history-block">
          <button type="button" className="history-toggle" onClick={() => setShowHistory((value) => !value)}>
            <span>История пропущенных выпусков</span><strong>{showHistory ? "−" : "+"}</strong>
          </button>
          {showHistory && <div className="history-list">{history.map((item) => <div key={item.id}><span>{hobbyPresentation[item.hobby.code].emoji}</span><p><strong>{item.card.title ?? item.hobby.name}</strong><small>Остался в истории без ответа</small></p></div>)}</div>}
        </div>
      )}
    </section>
  );
}

function ProfileView({
  me,
  accesses,
  notify,
  invite,
  support,
  dataRequest,
  subscriptions,
  manageSubscription,
  monthlyReport
}: {
  me: UserProfile;
  accesses: AccessView[];
  notify: (message: string) => void;
  invite: () => void;
  support: () => void;
  dataRequest: () => void;
  subscriptions: SubscriptionView[];
  manageSubscription: (subscription: SubscriptionView) => void;
  monthlyReport?: MonthlyReportView;
}) {
  return (
    <section className="content-view">
      <header className="profile-header glass-card">
        <div className="avatar">{me.firstName?.charAt(0).toUpperCase() ?? "Н"}</div>
        <div><p className="eyebrow">Ваш профиль</p><h1>{me.firstName ?? "Любопытный человек"}</h1><p>С нами с {formatDate(me.createdAt, { year: "numeric" })}</p></div>
      </header>
      {monthlyReport && (
        <div className="profile-section">
          <h2>Ваши последние 30 дней</h2>
          <article className="monthly-report-card glass-card">
            <div className="report-period">
              <span>Отчёт до {formatDate(monthlyReport.windowEnd)}</span>
              <strong>{monthlyReport.tasksMissed === 0 && monthlyReport.tasksReceived > 0 ? "Без пропусков ✨" : "Ваш ритм"}</strong>
            </div>
            <div className="report-metrics">
              <div><strong>{monthlyReport.tasksCompleted}</strong><small>сохранено</small></div>
              <div><strong>{monthlyReport.tasksMissed}</strong><small>пропущено</small></div>
              <div><strong>{monthlyReport.currentStreak}</strong><small>серия</small></div>
            </div>
            <p>Всего в коллекции: {monthlyReport.collectionSize}</p>
            {monthlyReport.perHobby.length > 0 && (
              <div className="report-hobbies">
                {monthlyReport.perHobby.map((hobby) => (
                  <span key={hobby.id}>{hobbyPresentation[hobby.code].emoji} {hobby.name}: {hobby.completed}/{hobby.received}</span>
                ))}
              </div>
            )}
          </article>
        </div>
      )}
      <div className="profile-section">
        <h2>Мои доступы</h2>
        {accesses.filter((access) => access.status === "active" && access.type !== "paid").map((access) => (
          <div className="profile-row glass-card" key={access.id}>
            <span>{hobbyPresentation[access.hobby.code].emoji}</span><div><strong>{access.hobby.name}</strong><small>{access.hobby.status === "exhausted"
              ? "Все доступные выпуски пройдены"
              : access.type === "free" ? "Бесплатное хобби" : "Временный доступ"}</small></div><b>{access.hobby.status === "exhausted" ? "Готово" : "Активно"}</b>
          </div>
        ))}
        {subscriptions.map((subscription) => (
          <button type="button" className="profile-row profile-row-button glass-card" key={subscription.id} onClick={() => manageSubscription(subscription)}>
            <span>{hobbyPresentation[subscription.hobby.code].emoji}</span>
            <div>
              <strong>{subscription.hobby.name}</strong>
              <small>{subscription.contentExhaustedAt
                ? "Все выпуски пройдены · продление отключено"
                : subscription.status === "expired"
                ? "Подписка завершена"
                : subscription.autoRenewEnabled
                  ? `${subscription.priceStars} ⭐ · продление ${formatDate(subscription.currentPeriodEnd)}`
                  : `Продление отключено · до ${formatDate(subscription.retentionEndsAt ?? subscription.currentPeriodEnd)}`}</small>
            </div>
            <b>›</b>
          </button>
        ))}
      </div>
      <div className="profile-section">
        <h2>Настройки</h2>
        <div className="settings-card glass-card">
          <button type="button" onClick={() => notify("Время и часовой пояс определяются автоматически при каждом открытии приложения. Ничего настраивать вручную не нужно.")}><span>🕒</span><p><strong>Время и часовой пояс</strong><small>{me.timezone ?? "Определяется автоматически"}</small></p><b className="help-mark">?</b></button>
          <button type="button" onClick={invite}><span>👥</span><p><strong>Пригласить друга</strong><small>Отправить личную ссылку</small></p><b>›</b></button>
          <button type="button" onClick={support}><span>💬</span><p><strong>Поддержка</strong><small>Создать обращение в Telegram</small></p><b>›</b></button>
          <button type="button" onClick={dataRequest}><span>🗂️</span><p><strong>Мои данные</strong><small>Получить копию, исправить или удалить</small></p><b>›</b></button>
          <button type="button" onClick={() => openExternalLink(me.legal.termsUrl)}><span>📄</span><p><strong>Условия использования</strong><small>Актуальная версия {me.legal.termsVersion}</small></p><b>↗</b></button>
          <button type="button" onClick={() => openExternalLink(me.legal.privacyUrl)}><span>🔒</span><p><strong>Конфиденциальность</strong><small>Какие данные нужны приложению</small></p><b>↗</b></button>
        </div>
      </div>
      <p className="profile-footnote">Нишевые хобби · ваша личная коллекция</p>
    </section>
  );
}

function SubscriptionSheet({
  subscription,
  busy,
  close,
  cancel,
  reenable,
  continueHobby,
  chooseAnother
}: {
  subscription: SubscriptionView;
  busy: boolean;
  close: () => void;
  cancel: (subscription: SubscriptionView, acceptRetention: boolean) => Promise<void>;
  reenable: (subscription: SubscriptionView) => Promise<void>;
  continueHobby: (subscription: SubscriptionView) => Promise<void>;
  chooseAnother: () => void;
}) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const endDate = subscription.retentionEndsAt ?? subscription.currentPeriodEnd;
  return (
    <Sheet onClose={close} label="Управление подпиской">
      <Cover code={subscription.hobby.code} />
      <div className="sheet-content subscription-content">
        <p className="eyebrow">Управление подпиской</p>
        <h2>{subscription.hobby.name}</h2>
        {subscription.contentExhaustedAt ? (
          <>
            <p className="detail-copy">Вы получили все доступные выпуски этого хобби. Продление отключено, новых списаний не будет, а коллекция останется с вами.</p>
            <div className="sheet-actions">
              <Button onClick={chooseAnother}>Выбрать другое хобби</Button>
            </div>
          </>
        ) : subscription.status === "expired" ? (
          <>
            <p className="detail-copy">{subscription.canceledAt
              ? "Вы отключили продление. Оплаченный период закончился, новые выпуски приостановлены, а коллекция сохранена."
              : "Последнее списание не прошло. Новые выпуски приостановлены, а коллекция сохранена."}</p>
            <div className="sheet-actions"><Button onClick={() => void continueHobby(subscription)} disabled={busy}>
              {subscription.canceledAt ? "Подключить снова" : "Повторить оплату"} · {subscription.priceStars} ⭐
            </Button></div>
          </>
        ) : subscription.autoRenewEnabled ? confirmCancel ? (
          <>
            <div className="retention-offer">
              <span>Небольшой подарок перед паузой</span>
              <h3>Ещё 14 дней бесплатно</h3>
              <p>Отключите продление сейчас — после оплаченного периода получите ещё 14 дней без списаний.</p>
            </div>
            <div className="sheet-actions">
              {subscription.retentionAvailable && <Button onClick={() => void cancel(subscription, true)} disabled={busy}>Остаться ещё на 14 дней</Button>}
              <Button kind="secondary" onClick={() => void cancel(subscription, false)} disabled={busy}>Отключить без подарка</Button>
              <Button kind="ghost" onClick={() => setConfirmCancel(false)} disabled={busy}>Оставить подписку</Button>
            </div>
          </>
        ) : (
          <>
            <p className="detail-copy">Следующее продление — {formatDate(subscription.currentPeriodEnd)}. Стоимость: {subscription.priceStars} ⭐ за 30 дней.</p>
            <div className="sheet-actions">
              <Button kind="secondary" onClick={() => setConfirmCancel(true)}>Отключить автопродление</Button>
            </div>
          </>
        ) : (
          <>
            <p className="detail-copy">Продление отключено. Доступ сохранится до {formatDate(endDate)}, коллекция останется с вами.</p>
            <div className="sheet-actions">
              <Button onClick={() => void reenable(subscription)} disabled={busy}>Включить продление снова</Button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}

function ReferralSheet({ referral, close, notify }: { referral: ReferralView; close: () => void; notify: (message: string) => void }) {
  const shareText = "Станем невыносимо интересными? Выбери хобби, сделай первое открытие — и мы оба получим ещё одно на 30 дней ✨";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(referral.invitationUrl);
      haptic("success");
      notify("Личная ссылка скопирована.");
    } catch {
      notify("Не удалось скопировать ссылку. Нажмите «Отправить другу».");
    }
  };
  return (
    <Sheet onClose={close} label="Пригласить друга">
      <div className="confirm-visual"><span>👥</span></div>
      <div className="sheet-content referral-content">
        <p className="eyebrow">Ваша личная ссылка</p>
        <h2>Один друг — два новых хобби</h2>
        <p className="detail-copy">Когда друг выберет бесплатное хобби и выполнит первое задание, вы оба сможете открыть ещё по одному хобби на 30 дней.</p>
        <div className="referral-link">{referral.invitationUrl}</div>
        <div className="sheet-actions">
          <Button onClick={() => shareTelegramLink(referral.invitationUrl, shareText)}>Отправить другу</Button>
          <Button kind="secondary" onClick={() => void copy()}>Скопировать ссылку</Button>
        </div>
      </div>
    </Sheet>
  );
}

function ResultSheet({ detail, close, openNoteInBot }: { detail: CollectionDetail; close: () => void; openNoteInBot: (taskId: string, responseId: string) => void }) {
  const item = detail.item;
  return (
    <Sheet onClose={close} label="Результат">
      <Cover code={item.hobby.code} card={item.card} />
      <div className="sheet-content result-content">
        <SourcePreview card={item.card} />
        <p className="eyebrow">{item.hobby.name} · выпуск {String(item.sequenceNo).padStart(2, "0")}</p>
        <h2>{item.card.title ?? item.hobby.name}</h2>
        <RichCardContent card={item.card} />
        <div className="response-list">
          <h3>Ваши заметки</h3>
          {detail.responses.map((response) => response.type === "text" ? (
            <button type="button" className="note-open" key={response.id} onClick={() => openNoteInBot(item.id, response.id)}><blockquote>{response.text}<small>{formatDateTime(response.receivedAt)} · открыть в Telegram</small></blockquote></button>
          ) : (
            <button type="button" className="voice-note note-open" key={response.id} onClick={() => openNoteInBot(item.id, response.id)}><span>▶</span><p><strong>Голосовая заметка</strong><small>{response.voiceDurationSeconds ?? 0} секунд · открыть в Telegram</small></p></button>
          ))}
        </div>
        <Button kind="secondary" onClick={close}>Вернуться в коллекцию</Button>
      </div>
    </Sheet>
  );
}

export function App() {
  const started = useRef(false);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>("today");
  const [hobbies, setHobbies] = useState<HobbySummary[]>([]);
  const [me, setMe] = useState<UserProfile | null>(null);
  const [overview, setOverview] = useState<Overview>({ activeTasks: [], progress: { completed: 0, missed: 0, activeHobbies: 0 } });
  const [accesses, setAccesses] = useState<AccessView[]>([]);
  const [collection, setCollection] = useState<TaskView[]>([]);
  const [history, setHistory] = useState<TaskView[]>([]);
  const [result, setResult] = useState<CollectionDetail | null>(null);
  const [referral, setReferral] = useState<ReferralView | null>(null);
  const [referralRewards, setReferralRewards] = useState<ReferralRewardView[]>([]);
  const [monthlyReports, setMonthlyReports] = useState<MonthlyReportView[]>([]);
  const [regularityRewards, setRegularityRewards] = useState<RegularityRewardView[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionView[]>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<SubscriptionView | null>(null);
  const [toast, setToast] = useState("");

  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", []);
  const deepLinkHobbyId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("view") === "hobbies" && params.get("action") === "subscribe"
      ? params.get("hobby")
      : null;
  }, []);
  const onboarded = Boolean(me?.freeChoice.startedAt);

  const notify = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 3200);
  }, []);

  const refresh = useCallback(async () => {
    const [profile, nextOverview, nextAccesses, nextCollection, nextHistory, nextSubscriptions, nextRewards, reportData] = await Promise.all([
      api.me(), api.overview(), api.accesses(), api.collection(), api.history(), api.subscriptions(), api.referralRewards(), api.monthlyReports()
    ]);
    setMe(profile);
    setOverview(nextOverview);
    setAccesses(nextAccesses);
    setCollection(nextCollection);
    setHistory(nextHistory);
    setSubscriptions(nextSubscriptions);
    setReferralRewards(nextRewards);
    setMonthlyReports(reportData.reports);
    setRegularityRewards(reportData.rewards);
    return profile;
  }, []);

  const initialize = useCallback(async () => {
    setLoadState("loading");
    setError("");
    try {
      await createSession(telegramWebApp()?.initData ?? "", timezone);
      const catalog = await api.catalog();
      setHobbies(catalog.hobbies);
      const profile = await refresh();
      setActiveTab(profile.freeChoice.startedAt ? "today" : "hobbies");
      setLoadState("ready");
    } catch (caught) {
      const message = caught instanceof ApiError
        ? caught.message
        : import.meta.env.VITE_ENABLE_LOCAL_AUTH === "true"
          ? "Проверьте, что локальный Worker запущен, и попробуйте снова."
          : "Закройте Mini App и откройте заново из бота. Если ошибка повторится, обратитесь в поддержку.";
      setError(message);
      setLoadState("error");
    }
  }, [refresh, timezone]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void initialize();
  }, [initialize]);

  useEffect(() => {
    if (loadState !== "ready" || !onboarded) return;
    const params = new URLSearchParams(window.location.search);
    const view = params.get("view");
    const taskId = params.get("task");
    if (view === "collection") setActiveTab("collection");
    if (view === "today") setActiveTab("today");
    if (view === "hobbies") setActiveTab("hobbies");
    if (view === "profile") setActiveTab("profile");
    if (taskId && view === "collection") void openResult(taskId);
    const subscriptionId = params.get("subscription");
    if (subscriptionId) setSelectedSubscription(subscriptions.find((item) => item.id === subscriptionId) ?? null);
  }, [loadState, onboarded, subscriptions]);

  const activate = async (hobby: HobbySummary) => {
    setBusy(true);
    try {
      await api.activateFree(hobby.id, timezone);
      await refresh();
      haptic("success");
      setActiveTab("today");
      notify("Хобби началось. Первый выпуск уже ждёт вас в боте.");
    } catch (caught) {
      haptic("warning");
      notify(caught instanceof Error ? caught.message : "Не удалось начать хобби");
    } finally {
      setBusy(false);
    }
  };

  const changeHobby = async (hobby: HobbySummary) => {
    setBusy(true);
    try {
      await api.changeFree(hobby.id, timezone);
      await refresh();
      haptic("success");
      setActiveTab("today");
      notify(`Теперь ваше бесплатное хобби — «${hobby.name}».`);
    } catch (caught) {
      haptic("warning");
      notify(caught instanceof Error ? caught.message : "Не удалось заменить хобби");
    } finally {
      setBusy(false);
    }
  };

  const returnToTelegram = async (taskId: string, responseId?: string) => {
    try {
      await api.returnToTelegram(taskId, responseId);
      haptic("light");
      if (!closeToBotChat()) notify("Для локального просмотра чат не подключён.");
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось открыть карточку в Telegram");
    }
  };

  const notifyNewHobbies = async () => {
    try {
      const preference = await api.notifyNewHobbies();
      haptic("success");
      setMe((current) => current ? {
        ...current,
        notifications: { ...current.notifications, newHobbies: true }
      } : current);
      notify(preference.alreadyEnabled
        ? "Уведомления уже включены. Мы напишем о каждом новом хобби."
        : "Готово. Будем писать о каждом новом хобби.");
      return true;
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось включить уведомление");
      return false;
    }
  };

  const inviteFriend = async () => {
    setBusy(true);
    try {
      setReferral(await api.referral());
      haptic("light");
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось создать ссылку");
    } finally {
      setBusy(false);
    }
  };

  const openSupport = async () => {
    setBusy(true);
    try {
      await api.startSupport();
      haptic("light");
      if (!closeToBotChat()) notify("Откройте чат с ботом — там уже ждёт форма обращения.");
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось открыть поддержку");
    } finally {
      setBusy(false);
    }
  };

  const openDataRequest = async () => {
    setBusy(true);
    try {
      await api.startDataRequest();
      haptic("light");
      if (!closeToBotChat()) notify("Откройте чат с ботом и выберите, что сделать с данными.");
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось открыть запрос");
    } finally {
      setBusy(false);
    }
  };

  const acceptLegal = async () => {
    setBusy(true);
    try {
      await api.acceptLegal();
      setMe((current) => current ? { ...current, legal: { ...current.legal, accepted: true } } : current);
      haptic("success");
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось сохранить согласие");
    } finally {
      setBusy(false);
    }
  };

  const purchaseHobby = async (hobby: HobbySummary) => {
    setBusy(true);
    try {
      const checkout = await api.createCheckout(hobby.id, timezone);
      const opened = openTelegramInvoice(checkout.invoiceUrl, async (invoiceStatus) => {
        if (invoiceStatus !== "paid") {
          if (invoiceStatus === "failed") notify("Оплата не завершилась. Попробуйте ещё раз.");
          return;
        }
        for (let attempt = 0; attempt < 5; attempt += 1) {
          const status = await api.checkoutStatus(checkout.checkoutSessionId);
          if (status.status === "paid") {
            await refresh();
            setActiveTab("today");
            haptic("success");
            notify(`«${hobby.name}» подключено. Первый выпуск уже готов.`);
            return;
          }
          await new Promise((resolve) => window.setTimeout(resolve, 700));
        }
        notify("Платёж принят. Выпуск появится через несколько секунд.");
      });
      if (opened) void api.markCheckoutOpened(checkout.checkoutSessionId).catch(() => undefined);
      if (!opened) notify("Оплата Stars доступна только внутри Telegram.");
    } catch (caught) {
      haptic("warning");
      notify(caught instanceof Error ? caught.message : "Не удалось открыть оплату");
    } finally {
      setBusy(false);
    }
  };

  const activateReferralReward = async (reward: ReferralRewardView, hobby: HobbySummary) => {
    setBusy(true);
    try {
      await api.activateReferralReward(reward.id, hobby.id);
      await refresh();
      setActiveTab("today");
      haptic("success");
      notify(`Подарок активирован: «${hobby.name}» открыто на 30 дней.`);
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось открыть подарочное хобби");
    } finally {
      setBusy(false);
    }
  };

  const activateRegularityReward = async (reward: RegularityRewardView, hobby: HobbySummary) => {
    setBusy(true);
    try {
      await api.activateRegularityReward(reward.id, hobby.id);
      await refresh();
      setActiveTab("today");
      haptic("success");
      notify(`Бонус активирован: «${hobby.name}» открыто на 30 дней.`);
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось открыть бонусное хобби");
    } finally {
      setBusy(false);
    }
  };

  const cancelSubscription = async (subscription: SubscriptionView, acceptRetention: boolean) => {
    setBusy(true);
    try {
      await api.cancelSubscription(subscription.id, acceptRetention);
      await refresh();
      setSelectedSubscription(null);
      haptic("success");
      notify(acceptRetention ? "Продление отключено. 14 бесплатных дней начнутся после оплаченного периода." : "Автопродление отключено.");
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось отключить продление");
    } finally {
      setBusy(false);
    }
  };

  const reenableSubscription = async (subscription: SubscriptionView) => {
    setBusy(true);
    try {
      await api.reenableSubscription(subscription.id);
      await refresh();
      setSelectedSubscription(null);
      haptic("success");
      notify("Автопродление снова включено.");
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось включить продление");
    } finally {
      setBusy(false);
    }
  };

  const continueSubscription = async (subscription: SubscriptionView) => {
    const hobby = hobbies.find((item) => item.id === subscription.hobby.id);
    if (!hobby) return notify("Хобби пока недоступно для подключения");
    setSelectedSubscription(null);
    await purchaseHobby(hobby);
  };

  const openResult = async (taskId: string) => {
    try {
      setResult(await api.collectionDetail(taskId));
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "Не удалось открыть результат");
    }
  };

  if (loadState === "loading") return <LoadingScreen />;
  if (loadState === "error") return <ErrorScreen message={error} retry={() => void initialize()} />;
  if (!me) return null;
  if (!onboarded) return <Onboarding hobbies={hobbies} busy={busy} onActivate={activate} onNotifyNewHobbies={notifyNewHobbies} newHobbyNotificationsEnabled={me.notifications.newHobbies} />;
  if (!me.legal.accepted) return <LegalConsent me={me} busy={busy} accept={() => void acceptLegal()} />;

  return (
    <div className="mini-app">
      <main className="main-content">
        {activeTab === "today" && <TodayView overview={overview} openResult={(id) => void openResult(id)} answer={(id) => void returnToTelegram(id)} />}
        {activeTab === "hobbies" && <HobbiesView hobbies={hobbies} accesses={accesses} {...(deepLinkHobbyId ? { deepLinkHobbyId } : {})} canChange={me.freeChoice.canChange} busy={busy} onChange={changeHobby} onPurchase={purchaseHobby} onPaywallView={(hobby) => { void api.trackPaywallView(hobby.id).catch(() => undefined); }} onOpenToday={() => setActiveTab("today")} onNotifyNewHobbies={notifyNewHobbies} newHobbyNotificationsEnabled={me.notifications.newHobbies} onActivateReferralReward={activateReferralReward} onActivateRegularityReward={activateRegularityReward} {...(referralRewards.find((reward) => reward.status === "available") ? { referralReward: referralRewards.find((reward) => reward.status === "available")! } : {})} {...(regularityRewards.find((reward) => reward.status === "earned") ? { regularityReward: regularityRewards.find((reward) => reward.status === "earned")! } : {})} />}
        {activeTab === "collection" && <CollectionView items={collection} history={history} openResult={(id) => void openResult(id)} answer={(id) => void returnToTelegram(id)} {...(overview.activeTasks[0]?.id ? { activeTaskId: overview.activeTasks[0].id } : {})} />}
        {activeTab === "profile" && <ProfileView me={me} accesses={accesses} subscriptions={subscriptions} manageSubscription={setSelectedSubscription} notify={notify} invite={() => void inviteFriend()} support={() => void openSupport()} dataRequest={() => void openDataRequest()} {...(monthlyReports[0] ? { monthlyReport: monthlyReports[0] } : {})} />}
      </main>
      <nav className="bottom-nav" aria-label="Основная навигация">
        {tabs.map((tab) => (
          <button type="button" key={tab.id} className={activeTab === tab.id ? "active" : ""} onClick={() => { haptic("light"); setActiveTab(tab.id); }}>
            <span>{tab.emoji}</span><small>{tab.label}</small>
          </button>
        ))}
      </nav>
      {result && <ResultSheet detail={result} close={() => setResult(null)} openNoteInBot={(taskId, responseId) => void returnToTelegram(taskId, responseId)} />}
      {referral && <ReferralSheet referral={referral} close={() => setReferral(null)} notify={notify} />}
      {selectedSubscription && <SubscriptionSheet
        subscription={selectedSubscription}
        busy={busy}
        close={() => setSelectedSubscription(null)}
        cancel={cancelSubscription}
        reenable={reenableSubscription}
        continueHobby={continueSubscription}
        chooseAnother={() => {
          setSelectedSubscription(null);
          setActiveTab("hobbies");
        }}
      />}
      {toast && <div className="toast" role="status">{toast}</div>}
    </div>
  );
}
